import React from 'react';
import PropTypes from 'prop-types';

import useAppContext from '../../../../../context/useAppContext';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

import { Table, TableCellDropdown, Icon, Status, Tooltip, InfoText, FormatDate } from '@weezevent/nacre';
import Style from './../../list.module.css';
import { ReactComponent as Seat } from '@weezevent/throne/dist/images/seating/siege-available.svg';
import { useHistory } from 'react-router-dom';
import { InfoTextTooltip } from '../../../detail/components/infotext-tooltip/infoTextTooltip';
import { ListDropdownItems } from '../../list';
import { AttendeeModerationStatus } from '../moderation-status';

export const AttendeeDate = React.memo(({ date }) => {
    const [{ event }] = useAppContext();

    const dateObject = new Date(date);
    //@Todo useless ?
    const tzDate = moment(dateObject).tz(event.timezone, true);

    const formatter_date = new Intl.DateTimeFormat(event.default_language, { dateStyle: 'short' });
    const formatted_date = formatter_date.format(tzDate);
    const formatter_time = new Intl.DateTimeFormat(event.default_language, { timeStyle: 'short' });
    const formatted_time = formatter_time.format(tzDate);

    return <InfoTextTooltip title={formatted_date} subtitle={formatted_time} />;
});

AttendeeDate.propTypes = {
    date: PropTypes.string.isRequired
};

const AttendeeRate = React.memo(({ rate }) => {
    let groupLabel = rate.group ? (rate.group.group_template ? rate.group.group_template.name : rate.group.name) : null;

    return <InfoTextTooltip title={rate.name} subtitle={groupLabel} />;
});

AttendeeRate.propTypes = {
    rate: PropTypes.object.isRequired
};

const SubLabelSeating = React.memo(({ booking }) => {
    const { t } = useTranslation();

    //Check free seating
    if (!booking.seat_uuid && booking.area_zone) {
        return <span>{t('ticket.common.seating.freeSeating')}</span>;
    }

    return (
        <span>
            <span className={`${Style['bold']} ${Style['inline']}`}>{t('ticket.manager.attendees.shortLabelSeat')}</span>
            {`${booking.seat_seat} • `}
            <span className={`${Style['bold']} ${Style['inline']}`}>{t('ticket.manager.attendees.shortLabelRow')}</span>
            {`${booking.seat_row} • `}
            <span className={`${Style['bold']} ${Style['inline']}`}>{t('ticket.manager.attendees.shortLabelAccess')}</span>
            {booking.seat_access}
        </span>
    );
});

SubLabelSeating.propTypes = {
    booking: PropTypes.object.isRequired
};

const AttendeeSeating = React.memo(({ booking, mode = 'default' }) => {
    const { t } = useTranslation();

    const sizingIconSeat = React.useMemo(() => {
        return {
            width: mode === 'mini' ? '15px' : '12px',
            height: mode === 'mini' ? '14px' : '11px'
        };
    }, [mode]);

    if (!booking) {
        return <InfoTextTooltip title={t('ticket.common.seating.unseatedTicket')} />;
    }

    const titleTooltipSeating = booking => {
        //Check free seating
        if (!booking.seat_uuid && booking.area_zone) {
            return t('ticket.manager.attendees.tooltipSeating.freeSeating', { category: booking.rate_category_name, zone: booking.area_zone_name });
        }

        return t('ticket.manager.attendees.tooltipSeating.seating', {
            category: booking.rate_category_name,
            zone: booking.seat_zone_name,
            seat: booking.seat_seat,
            row: booking.seat_row,
            access: booking.seat_access
        });
    };

    return (
        <InfoText subtitle={mode !== 'mini' ? <SubLabelSeating booking={booking} /> : null}>
            <div className={Style['seating-info']}>
                <style>{`
                    svg.seat-category {
                        width: ${sizingIconSeat.width};
                        height: ${sizingIconSeat.height};
                        margin-right: 3px;
                   }

                    svg.seat-category-${booking.rate_category_color?.substring(1, booking.rate_category_color.length)} path {
                        fill: ${booking.rate_category_color};
                   }
                `}</style>
                <Tooltip triggerOnHover title={<span style={{ whiteSpace: 'break-spaces' }}>{titleTooltipSeating(booking)}</span>} margin={false} position={'top-start'}>
                    <Seat className={`seat-category ${'seat-category-' + booking.rate_category_color?.substring(1, booking.rate_category_color.length)}`} />
                </Tooltip>
                {mode !== 'mini' && <span>{!booking.seat_uuid && booking.area_zone ? booking.area_zone_name : booking.seat_zone_name}</span>}
            </div>
        </InfoText>
    );
});

AttendeeSeating.propTypes = {
    booking: PropTypes.object,
    mode: PropTypes.string
};

export const AttendeeStatus = React.memo(({ attendee, order = null, className = '', ledMode = false }) => {
    const { t } = useTranslation();

    const _order = React.useMemo(() => {
        return order || attendee?.order;
    }, [attendee, order]);

    const status = React.useMemo(() => {
        if (attendee?.removed) {
            return { color: 'red', status: t('ticket.manager.status.deleted') };
        }

        if (_order.status === 'reservation') {
            return { color: 'orange', status: t('ticket.manager.status.reserved') };
        }

        if (_order.status === 'unknown' || attendee?.refund_ticket_office) {
            return { color: 'grey', status: t('ticket.manager.status.inactive') };
        }

        if (_order.status === 'valid') {
            return { color: 'purple', status: t('ticket.manager.status.active') };
        }
        return { color: 'red', status: 'error' };
    }, [attendee, _order]);

    return !ledMode ? (
        <Status className={className} responsive text={status.status} color={status.color} />
    ) : (
        <Tooltip triggerOnHover position={'top-end'} title={status.status} margin={false}>
            <Status className={className} responsive color={status.color} />
        </Tooltip>
    );
});

export const InfoTextToolTipMemo = React.memo(({ title = '', subtitle = '', tooltipCopy }) => {
    return <InfoTextTooltip title={title} subtitle={subtitle} tooltipCopy={tooltipCopy} />;
});

const TableCellDropDownMemo = React.memo(({ attendee, prefix, onChangeAction, history }) => {
    return (
        <TableCellDropdown className={Style['width-x-mini']} disabled={attendee.order.status === 'unknown'}>
            <ListDropdownItems key="list" attendee={attendee} onChangeAction={onChangeAction} prefix={prefix} history={history} />
        </TableCellDropdown>
    );
});

AttendeeStatus.propTypes = {
    attendee: PropTypes.object.isRequired,
    className: PropTypes.string
};

const AttendeeSession = React.memo(({ booking, lang }) => {
    const date = booking?.inventory_start_date;

    return date ? (
        <>
            <InfoTextTooltip
                title={
                    <>
                        <Icon name={'calendar'} /> <FormatDate date={date} format="date-with-time" locale={lang} language={lang} />
                    </>
                }
            />
        </>
    ) : null;
});

AttendeeSession.propTypes = {
    booking: PropTypes.object.isRequired,
    lang: PropTypes.string
};

// Index is important, it allows drag&drop and other stuff with new table
export const AttendeeRow = React.memo(({ index, attendee, isSeatingEvent = false, onSelected = () => {}, isChecked = false, onChangeAction = () => {}, sync = () => {} }) => {
    const [{ event, organization }] = useAppContext();
    const history = useHistory();

    const prefix = React.useMemo(() => {
        return `/ticket/O${organization.id}/E${event.id}/manager`;
    }, [event, organization]);
    const backgroundClasses = {
        rejected: 'attendee-row__declined',
        validated: 'attendee-row__accepted',
        uncomplete: 'attendee-row__uncomplete',
        pending: 'attendee-row__pending'
    };

    const _moderationClassName = React.useMemo(() => {
        return event.is_moderation_enabled ? backgroundClasses[attendee.moderation_status] : '';
    }, [attendee, event]);

    return (
        <Table.Tr
            active={isChecked}
            onSelected={(event, data) => onSelected({ event, data }, attendee)}
            className={`${Style['tr-body']} ${attendee.removed ? Style['tr-deleted'] : ''} ${attendee.order.status === 'unknown' ? Style['tr-unknown'] : ''} ${
                event.is_moderation_enabled ? Style[_moderationClassName] : ''
            }`}
            onClick={() => history.push(`${prefix}/attendees/${attendee.pk}`)}
        >
            <Table.Td className={`${Style['width-small']} ${Style['list__td-info-text']}`}>
                <AttendeeDate date={attendee.order.sold_date} />
            </Table.Td>
            <Table.Td className={`${Style['width-x-large']} ${Style['list__td-info-text']}`}>
                <InfoTextToolTipMemo tooltipCopy title={attendee.resolved_name} subtitle={attendee.resolved_mail} />
            </Table.Td>
            <Table.Td className={`${Style['width-regular']} ${Style['list__td-info-text']}`}>
                <InfoTextToolTipMemo tooltipCopy title={attendee.barcode} subtitle={attendee.id_weez_ticket} />
            </Table.Td>
            <Table.Td className={`${Style['width-x-large']} ${Style['list__td-info-text']}`}>
                <AttendeeRate rate={attendee.rate} />
            </Table.Td>
            {isSeatingEvent && (
                <Table.Td className={`${Style['width-x-large']} ${Style['list__td-info-text']}`}>
                    <AttendeeSeating booking={attendee.booking} mode={'default'} />
                </Table.Td>
            )}
            {event.use_sessions && (
                <Table.Td className={`${Style['width-x-large']} ${Style['list__td-info-text']}`}>
                    <AttendeeSession booking={attendee.booking} lang={event.default_language} />
                </Table.Td>
            )}
            <Table.Td className={`${Style['td-scanned']} ${Style['width-x-mini']}`}>
                {attendee.scanned ? <Icon name="check" size="small" inverted circular /> : <Icon className={Style['icon_not_scanned']} name="cross-circle" />}
            </Table.Td>
            <Table.Td className={Style['width-x-mini']}>
                <AttendeeStatus attendee={attendee} />
            </Table.Td>
            {event.is_moderation_enabled && (
                <Table.Td className={`${Style['moderation-status']}`}>
                    <AttendeeModerationStatus attendee={attendee} sync={sync} />
                </Table.Td>
            )}
            {event.is_moderation_enabled && <Table.Td className={`${Style['width-x-mini']}`}>{attendee.count_moderation_mail_sent || 0}</Table.Td>}
            <TableCellDropDownMemo attendee={attendee} prefix={prefix} onChangeAction={onChangeAction} history={history} />
        </Table.Tr>
    );
});

AttendeeRow.propTypes = {
    index: PropTypes.number.isRequired,
    attendee: PropTypes.object.isRequired,
    isSeatingEvent: PropTypes.bool,
    onSelected: PropTypes.func,
    isChecked: PropTypes.bool,
    onChangeModal: PropTypes.func
};
